import type { AiSettings } from '../types';
import Toggle from './Toggle';

interface Props {
  form: AiSettings;
  set: <K extends keyof AiSettings>(key: K, val: AiSettings[K]) => void;
}

export default function LeadCaptureTab({ form, set }: Props) {
  return (
    <div className="space-y-5">

      {/* Main toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 flex items-center justify-center rounded-xl shrink-0"
              style={{
                backgroundColor: form.lead_capture_enabled
                  ? 'rgba(200,169,110,0.12)'
                  : '#f8fafc',
              }}
            >
              <i
                className="ri-user-received-line text-xl"
                style={{ color: form.lead_capture_enabled ? '#C8A96E' : '#cbd5e1' }}
              />
            </div>
            <div>
              <p className="text-[#0f172a] font-semibold text-sm">Lead Capture</p>
              <p className="text-slate-400 text-xs mt-0.5 leading-relaxed max-w-sm">
                Ask for name &amp; email before the conversation starts. Visitor data is
                saved to your Leads table automatically.
              </p>
            </div>
          </div>
          <Toggle
            enabled={form.lead_capture_enabled}
            onChange={(v) => set('lead_capture_enabled', v)}
          />
        </div>

        {form.lead_capture_enabled && (
          <div className="mt-4 flex items-start gap-2.5 px-3.5 py-3 bg-emerald-50 rounded-xl border border-emerald-200/60">
            <i className="ri-checkbox-circle-line text-emerald-500 text-sm mt-0.5 shrink-0" />
            <p className="text-emerald-700/80 text-[11px] leading-relaxed">
              When enabled, every new chat session will prompt the visitor for their name and
              email before starting the conversation. Saved to{' '}
              <span className="font-mono font-semibold">marketing_leads</span> via backend wiring.
            </p>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm mb-4">How Lead Capture Works</h3>
        <div className="space-y-3">
          {[
            {
              step: '1',
              title: 'Visitor opens chat',
              desc: 'The AI greets the visitor and asks for their name and email before answering questions.',
              icon: 'ri-chat-1-line',
              active: form.lead_capture_enabled,
            },
            {
              step: '2',
              title: 'Data saved to Leads',
              desc: 'Name and email are stored in the marketing_leads table linked to your tenant.',
              icon: 'ri-database-2-line',
              active: form.lead_capture_enabled,
            },
            {
              step: '3',
              title: 'Conversation continues',
              desc: 'The assistant proceeds with the sales conversation using the lead\'s name for personalization.',
              icon: 'ri-robot-line',
              active: form.lead_capture_enabled,
            },
            {
              step: '4',
              title: 'You see leads in admin',
              desc: 'New leads appear in your Leads section tagged as AI-captured for follow-up.',
              icon: 'ri-user-star-line',
              active: form.lead_capture_enabled,
            },
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-3">
              <div
                className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 font-bold text-xs ${
                  item.active
                    ? 'bg-[#C8A96E]/15 text-[#C8A96E]'
                    : 'bg-slate-100 text-slate-300'
                }`}
              >
                {item.step}
              </div>
              <div className="flex-1 pt-0.5">
                <div className="flex items-center gap-2">
                  <i
                    className={`${item.icon} text-xs ${
                      item.active ? 'text-[#C8A96E]' : 'text-slate-300'
                    }`}
                  />
                  <p
                    className={`text-xs font-semibold ${
                      item.active ? 'text-[#0f172a]' : 'text-slate-400'
                    }`}
                  >
                    {item.title}
                  </p>
                </div>
                <p className="text-slate-400 text-[11px] mt-0.5 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview of lead form */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Lead Form Preview</h3>
        <div
          className={`rounded-xl border p-4 transition-all ${
            form.lead_capture_enabled
              ? 'border-[#C8A96E]/30 bg-[#C8A96E]/5'
              : 'border-slate-200 bg-slate-50 opacity-50'
          }`}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#C8A96E]/20">
              <i className="ri-robot-line text-[#C8A96E] text-xs" />
            </div>
            <p className="text-[#0f172a] text-xs font-medium">
              Before we chat, mind sharing your name?
            </p>
          </div>
          <div className="space-y-2 mb-3">
            <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
              <p className="text-slate-300 text-xs">Your name…</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
              <p className="text-slate-300 text-xs">your@email.com</p>
            </div>
          </div>
          <div
            className="rounded-lg py-2 text-center text-xs font-semibold"
            style={{
              backgroundColor: form.lead_capture_enabled ? '#C8A96E' : '#e2e8f0',
              color: form.lead_capture_enabled ? '#fff' : '#94a3b8',
            }}
          >
            Start chatting →
          </div>
        </div>
        {!form.lead_capture_enabled && (
          <p className="text-slate-400 text-[11px] text-center mt-2">
            Enable Lead Capture to activate this form.
          </p>
        )}
      </div>
    </div>
  );
}